import Configuration from '@/configuration';

/**
 * Origin allow-list for requests that carry the user's OAuth token.
 *
 * Some endpoints are not addressed by a URL the app builds itself: hub-gateway
 * returns fully-qualified URLs for the functions API (`invoke_url`,
 * `file_upload_url`). Those URLs are attacker-influenced by construction, so the
 * origin is validated before the `Authorization` header is attached. Without
 * this, a single bad value coming back from the API would exfiltrate the bearer
 * token to an arbitrary host.
 *
 * The token itself is always sent as an `Authorization` header, never as a query
 * string parameter (URLs end up in browser history, logs and `Referer`).
 */

const originOf = (url) => {
    try {
        return new URL(url).origin;
    } catch (error) {
        return null;
    }
};

const trustedOrigins = () => {
    return [
        Configuration.VUE_APP_FUNCTIONS_API_URL,
        Configuration.VUE_APP_GRAPHQL_URL,
        Configuration.VUE_APP_URL,
    ]
        .map(originOf)
        .filter((origin) => origin !== null);
};

/**
 * Returns the url unchanged when it points at a trusted origin, throws otherwise.
 * Fails closed: an unparsable or unconfigured origin is rejected.
 */
export const assertTrustedUrl = (url) => {
    const origin = originOf(url);

    if (origin === null || !trustedOrigins().includes(origin)) {
        throw new Error(
            `Refusing to send credentials to untrusted origin "${origin || url}". ` +
            'Check that VUE_APP_FUNCTIONS_API_URL is configured.',
        );
    }

    return url;
};
